import React from 'react'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { useUpdateTicketValidToDateMutation } from 'orders/reduxQueries'
import { getLowestValidToDay } from 'orders/utils/getLowestValidToDay'
import { DatePicker } from 'uiComponents/popups/datePickerInput'
import { usePermission } from 'admin/hocs'
import { IOrder, IOrderItem } from 'orders/schema'
import { LoaderWrapper } from 'uiComponents/loaders'
import { useHasFeature } from 'utils/useHasFeature'
import UpdateDialog from './updateDialog'
import { BaseRouteParams } from 'hocs'
import { SerializedError } from '@reduxjs/toolkit'
import { DateFormats, parseDate } from 'utils/dates'
import { showErrorMessage, showSuccessMessage } from 'uiComponents/message'

interface Props {
    orderItem: IOrderItem
    order: IOrder | null
    status: string
}

const ValidTo: React.FC<Props> = ({ orderItem, order, status }) => {
    const extendOrderItemValidityFeatureFlag = useHasFeature({ featureName: 'extendOrderItemValidity' })
    const { accountSlug } = useParams<BaseRouteParams>()
    const [updateValidToDate, { isLoading, isError, error, isSuccess }] = useUpdateTicketValidToDateMutation()
    const [showUpdateDialog, setShowUpdateDialog] = React.useState(false)
    const [newDate, setNewDate] = React.useState<Date>()
    const { hasPermission } = usePermission()

    React.useEffect(() => {
        if (isError) {
            const responseError = error as SerializedError
            showErrorMessage(
                responseError.message ? responseError.message : 'Error updating valid to date',
                'update_valid_to_error',
            )
        } else if (isSuccess) {
            showSuccessMessage('Valid to date updated successfully', 'update_valid_to_success')
        }
    }, [isError, isSuccess])

    const onConfirm = async () => {
        if (newDate) {
            await updateValidToDate({
                accountSlug,
                orderItemUuid: orderItem.orderItemId,
                validTo: format(newDate, 'yyyy-MM-dd'),
            })
        }

        setShowUpdateDialog(false)
    }

    if (!orderItem.validTo) {
        return <>-</>
    }

    const validityCanBeEdited =
        extendOrderItemValidityFeatureFlag &&
        hasPermission('edit_orders', accountSlug) &&
        status !== 'refunded' &&
        status !== 'refunding' &&
        !orderItem.visitDate

    return (
        <LoaderWrapper loading={isLoading}>
            {showUpdateDialog && (
                <UpdateDialog
                    accountSlug={accountSlug}
                    orderItemUuid={orderItem.orderItemId}
                    order={order}
                    newDate={newDate!}
                    onCancel={() => {
                        setShowUpdateDialog(false)
                    }}
                    onConfirm={onConfirm}
                />
            )}
            <DatePicker
                date={orderItem.validTo ? parseDate(orderItem.validTo) : null}
                inlineEdit
                modalDatePicker
                lowerBoundary={getLowestValidToDay({
                    validFrom: orderItem.validFrom,
                    validTo: orderItem.validTo,
                })}
                onInlineEditAccept={(newDate) => {
                    setNewDate(newDate)
                    setShowUpdateDialog(true)
                }}
                disabled={!validityCanBeEdited}
                inlineEditDateFormat={DateFormats.LONG_DATE}
            />
        </LoaderWrapper>
    )
}

export default ValidTo
